/* eslint-disable no-nested-ternary */
import { ICompAnalysisRail } from "../types/ICompAnalysisRail";

export default function convertCompAnalysisRailData(
  // railRecordsArray: ICompAnalysisRail[]
  railRecordsArray: any[],
) {
  if (!Array.isArray(railRecordsArray)) {
    console.error("Input must be an array of railRecords.");
    return [];
  }

  /**
   * @typedef {object} InputRailLeg
   * @property {string | null} route
   * @property {number | undefined} rate
   * @property {number | undefined} fsc
   * @property {number | undefined} miles
   * @property {string | undefined} carrier
   * @property {string | undefined} document
   * @property {string | undefined} originCarrier
   * @property {string | undefined} destinationCarrier
   */

  return railRecordsArray.map((railRecord) => {
    const getSafe = (
      obj: ICompAnalysisRail,
      path: string,
      defaultValue = null,
    ) => {
      const keys = Array.isArray(path) ? path : path.split(".");

      const finalValue = (keys as string[]).reduce<any>(
        (currentObject: any, key: string) => {
          if (
            currentObject &&
            typeof currentObject === "object" &&
            key in currentObject
          ) {
            return (currentObject as Record<string, any>)[key];
          }
          return undefined;
        },
        obj,
      );

      return finalValue === undefined ? defaultValue : finalValue;
    };

    const commonLegExpiration = getSafe(railRecord, "railFreight.expiration");
    const commonLegMinCars = getSafe(railRecord, "railFreight.minCars");
    const commonLegCarOwner = getSafe(railRecord, "railFreight.carOwner");

    const transformedLegs = (getSafe(railRecord, "railFreight.legs") ?? []).map(
      (railLeg: {
        route: string;
        fsc: number;
        rate: number;
        miles: number;
        carrier: string;
        document: string;
        originCarrier: string;
        destinationCarrier: string;
      }) => {
        return {
          mode: "RAIL",
          fscRateUom: "",
          route: railLeg.route ?? null,
          fscRate: railLeg.fsc ?? null,
          rate: railLeg.rate ?? null,
          fsc: railLeg.fsc ?? null,
          dist: railLeg.miles ?? null,
          distUom: railLeg.miles !== undefined ? "/MI" : null,
          exp: commonLegExpiration,
          curr: "USD",
          rateUom: "ST",
          minCars: commonLegMinCars,
          carOwner: commonLegCarOwner,
          carrier: railLeg.carrier,
          document: railLeg.document,
          originCarrier: railLeg.originCarrier,
          destinationCarrier: railLeg.destinationCarrier,
        };
      },
    );

    const totalMiles = getSafe(railRecord, "railFreight.totalMiles");
    const record = {
      origin: {
        name: getSafe(railRecord, "origin.name"),
        owner: getSafe(railRecord, "origin.owner"),
        id: getSafe(railRecord, "origin.id"),
        coordinate: getSafe(railRecord, "origin.coordinate"),
        city: getSafe(railRecord, "origin.city"),
        station: getSafe(railRecord, "origin.station"),
      },
      destination: {
        name: getSafe(railRecord, "destination.name"),
        owner: getSafe(railRecord, "destination.owner"),
        id: getSafe(railRecord, "destination.id"),
        coordinate: getSafe(railRecord, "destination.coordinate"),
        city: getSafe(railRecord, "destination.city"),
        station: getSafe(railRecord, "destination.station"),
        address: getSafe(railRecord, "destination.address"),
      },
      occPartyName: getSafe(railRecord, "occupant.name"),
      occupantTypes: railRecord.competitor ? ["Competitor"] : ["Koch"],
      mode: "RAIL",
      legs: transformedLegs,
      fees: 0,
      rate: null,
      rateFactor: "ST",
      distance: totalMiles,
      freight: getSafe(railRecord, "freight"),
      isValid:
        getSafe(railRecord, "railFreight.validation.isValid", null) ?? false,
      minPrice: getSafe(railRecord, "minPrice"),
      maxPrice: getSafe(railRecord, "maxPrice"),
      price: getSafe(railRecord, "maxPrice") ?? 0,
      initialOffer: (railRecord.maxPrice ?? 0) + (railRecord.freight ?? 0),
      dateLogged: getSafe(railRecord, "indication.dateLogged"),
      comments: getSafe(railRecord, "indication.comments", null) ?? null,
      shipWindowStartDate: getSafe(
        railRecord,
        "indication.shipWindowStartDate",
      ),
      shipWindowEndDate: getSafe(railRecord, "indication.shipWindowEndDate"),
      contact: getSafe(railRecord, "indication.contact"),
      account: getSafe(railRecord, "indication.account"),
      marketer: getSafe(railRecord, "indication.marketer"),
      rateIssue: null,
      id: railRecord.id ?? null,
      railFreight: getSafe(railRecord, "railFreight"),
      validation: {
        sourceSystem: getSafe(
          railRecord,
          "railFreight.validation.sourceSystem",
        ),
        rate: getSafe(railRecord, "railFreight.validation.rate"),
        header: getSafe(railRecord, "railFreight.validation.header"),
        notes: getSafe(railRecord, "railFreight.validation.notes"),
        isValid: getSafe(railRecord, "railFreight.validation.isValid"),
      },
      costPerMile:
        railRecord.costPerMile !== undefined ? railRecord.costPerMile : null,
    };

    return record;
  });
}
